import * as React from "react";
import { Link } from "react-router-dom";
import { Modal, ModalBody, ModalFooter } from "../components/Modal";
import { Button, IconButton } from "../components/Button";
import { Banner } from "../components/States";
import { Checkbox, Field, Input, Select } from "../components/Form";
import { useAsync, useAction } from "../general/useAsync";
import { describeError } from "../api/errors";
import {
    applyCurriculumTemplate,
    CurriculumTemplateApplyOptions,
    CurriculumTemplateApplyResult,
    CurriculumTemplateCycleOption,
    CurriculumTemplateDraft,
    CurriculumTemplatePlannedAssessment,
    CurriculumTemplatePreview,
    CurriculumTemplatePreviewItem,
    CurriculumTemplateStructurePreview,
    discardCurriculumTemplateDraft,
    getCurriculumTemplateDraft,
    previewCurriculumTemplate,
    saveCurriculumTemplateDraft,
} from "./CurriculumTemplatesStore";

/**
 * Applying a curriculum template, as a wizard: structure, subjects, terms, weightings and rules, then a review.
 *
 * Every step's choices are saved to the server as the person moves on, so the wizard can be closed and picked up
 * later - by them or a colleague - from the step they were on. Nothing is laid down until the last step applies the
 * choices; applying clears the draft, and the choices used are kept with the application history.
 */

type StepKey = "structure" | "subjects" | "terms" | "weightings" | "assessments" | "review";

const STEP_LABELS: Record<StepKey, string> = {
    structure: "Structure",
    subjects: "Subjects",
    terms: "Terms",
    weightings: "Weightings & rules",
    assessments: "Assessments",
    review: "Review & apply",
};

const MAX_TERMS = 6;

export interface CurriculumTemplateWizardProps {
    templateKey: string;
    displayName: string;
    description?: string;
    canApply: boolean;
    onClose: () => void;
    /** Called after an apply, a saved step or a discarded draft, so the card can show the draft's state. */
    onChanged: () => void;
}

export const CurriculumTemplateWizard: React.FC<CurriculumTemplateWizardProps> = ({ templateKey, displayName, description, canApply, onClose, onChanged }) => {
    const preview = useAsync(() => previewCurriculumTemplate(templateKey), [templateKey]);
    const draft = useAsync(() => getCurriculumTemplateDraft(templateKey), [templateKey], { enabled: canApply });
    const [options, setOptions] = React.useState<CurriculumTemplateApplyOptions | null>(null);
    const [step, setStep] = React.useState<StepKey>("structure");
    const [saved, setSaved] = React.useState<CurriculumTemplateDraft | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState("");
    const [confirmDiscard, setConfirmDiscard] = React.useState(false);
    const [result, setResult] = React.useState<CurriculumTemplateApplyResult | null>(null);

    const data = preview.data;
    const steps = React.useMemo<StepKey[]>(() => {
        if (!data) return [];
        const list: StepKey[] = [];
        if (data.structure) list.push("structure");
        list.push("subjects", "terms");
        if (data.divisions.length > 0 || (data.structure?.choiceRuleNames?.length ?? 0) > 0) list.push("weightings");
        // Assessments are laid into the subjects' divisions, so the step needs both divisions and a structure to put them in.
        if (data.divisions.length > 0 && data.structure) list.push("assessments");
        list.push("review");
        return list;
    }, [data]);

    // The draft (or the template's defaults) seeds the wizard once both it and the template's contents have loaded.
    React.useEffect(() => {
        if (!draft.data || !data || options) return;
        setOptions(normalise(draft.data.options));
        setSaved(draft.data);
        const resumeAt = draft.data.currentStep as StepKey | undefined;
        setStep(resumeAt && steps.includes(resumeAt) ? resumeAt : steps[0]);
    }, [draft.data, data, steps, options]);

    const save = async (nextStep: StepKey) => {
        if (!options) return false;
        setSaving(true);
        setSaveError("");
        try {
            const stored = await saveCurriculumTemplateDraft(templateKey, nextStep, options);
            setSaved(stored);
            onChanged();
            return true;
        } catch (e) {
            setSaveError(describeError(e));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const goTo = async (target: StepKey) => {
        if (await save(target)) setStep(target);
    };

    const apply = useAction(async () => {
        if (!options) return;
        const applied = await applyCurriculumTemplate(templateKey, options);
        setResult(applied);
        onChanged();
    });

    const discard = useAction(async () => {
        await discardCurriculumTemplateDraft(templateKey);
        setConfirmDiscard(false);
        setOptions(null);
        draft.refresh();
        onChanged();
    });

    if (result) return <ApplyResult displayName={displayName} result={result} onClose={onClose} />;

    const loading = preview.isLoading || (canApply && (draft.isLoading || (!options && !draft.error)));
    const error = preview.error || (canApply ? draft.error : "");
    const index = steps.indexOf(step);
    const isLast = index === steps.length - 1;
    const problem = clientProblem(step, options, data) ?? null;

    const footer = (
        <ModalFooter>
            <div className="mr-auto flex items-center gap-3 text-xs text-text-weak">
                {saving ? "Saving..." : saved?.isSaved && saved.updatedAt ? `Draft saved ${new Date(saved.updatedAt).toLocaleString()}` : canApply && data?.hasSeedData ? "Not saved yet" : ""}
                {canApply && saved?.isSaved && !confirmDiscard && (
                    <button type="button" className="text-danger-text underline" onClick={() => setConfirmDiscard(true)}>
                        Discard draft
                    </button>
                )}
                {confirmDiscard && (
                    <span className="flex items-center gap-2">
                        Throw away these choices?
                        <Button size="sm" variant="danger" loading={discard.busy} onClick={discard.run}>
                            Discard
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setConfirmDiscard(false)}>
                            Keep
                        </Button>
                    </span>
                )}
            </div>
            {canApply && data?.hasSeedData && options ? (
                <>
                    <Button variant="secondary" onClick={async () => (await save(step)) && onClose()} disabled={saving}>
                        Save &amp; close
                    </Button>
                    {index > 0 && (
                        <Button variant="outline-secondary" onClick={() => goTo(steps[index - 1])} disabled={saving}>
                            Back
                        </Button>
                    )}
                    {isLast ? (
                        <Button variant="primary" loading={apply.busy} disabled={!!saved?.validationError || saving} onClick={apply.run}>
                            Apply template
                        </Button>
                    ) : (
                        <Button variant="primary" onClick={() => goTo(steps[index + 1])} loading={saving} disabled={!!problem}>
                            Next
                        </Button>
                    )}
                </>
            ) : (
                <Button variant="secondary" onClick={onClose}>
                    Close
                </Button>
            )}
        </ModalFooter>
    );

    return (
        <Modal isOpen title={`${displayName} template`} icon={["fad", "layer-group"]} size="xl" onClose={onClose} footer={footer}>
            <ModalBody>
                {description && (
                    <details className="mb-5 rounded-btn border border-border bg-surface-muted px-4 py-3">
                        <summary className="cursor-pointer text-sm font-medium text-text">About this template</summary>
                        <p className="mt-2 text-sm leading-relaxed text-text">{description}</p>
                    </details>
                )}
                {loading && <p className="text-sm text-text-weak">Loading...</p>}
                {error && <Banner tone="danger">{error}</Banner>}
                {!loading && data && !data.hasSeedData && (
                    <Banner tone="info">{"This template has no seed data configured yet — there is nothing to set up."}</Banner>
                )}
                {!loading && data?.hasSeedData && !canApply && (
                    <>
                        <Banner tone="warning" className="mb-4">
                            You don&apos;t have permission to apply curriculum templates. This is what the template contains.
                        </Banner>
                        <TemplateContents data={data} />
                    </>
                )}

                {!loading && data?.hasSeedData && canApply && options && (
                    <div className="space-y-4">
                        <ol className="flex flex-wrap gap-2" aria-label="Wizard steps">
                            {steps.map((s, i) => (
                                <li key={s}>
                                    <button
                                        type="button"
                                        onClick={() => s !== step && goTo(s)}
                                        disabled={saving}
                                        aria-current={s === step ? "step" : undefined}
                                        className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                                            s === step
                                                ? "bg-primary text-on-primary"
                                                : i < index
                                                  ? "bg-primary-soft text-primary-text hover:bg-primary-tint"
                                                  : "bg-surface-muted text-text-weak hover:bg-surface-hover"
                                        }`}
                                    >
                                        {i + 1}. {STEP_LABELS[s]}
                                    </button>
                                </li>
                            ))}
                        </ol>

                        {saved?.isStale && (
                            <Banner tone="warning">
                                This draft was started on an earlier version of the {displayName} template. Check the choices, or discard
                                the draft to start again from the current template.
                            </Banner>
                        )}
                        {saveError && <Banner tone="danger">{saveError}</Banner>}
                        {apply.error && <Banner tone="danger">{apply.error}</Banner>}
                        {problem && step !== "review" && <Banner tone="warning">{problem}</Banner>}

                        {step === "structure" && data.structure && (
                            <StructureStep structure={data.structure} hasProcedure={data.procedures.length > 0} options={options} onChange={setOptions} />
                        )}
                        {step === "subjects" && <SubjectsStep subjects={data.subjects} options={options} onChange={setOptions} />}
                        {step === "terms" && <TermsStep options={options} onChange={setOptions} />}
                        {step === "weightings" && <WeightingsStep data={data} options={options} onChange={setOptions} />}
                        {step === "assessments" && <AssessmentsStep data={data} options={options} onChange={setOptions} />}
                        {step === "review" && <ReviewStep data={data} options={options} validationError={saved?.validationError ?? undefined} />}
                    </div>
                )}
            </ModalBody>
        </Modal>
    );
};

// ------------------------------------------------------------------ steps

const StepIntro: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div>
        <h3 className="text-base font-semibold text-text-strong">{title}</h3>
        <p className="mt-0.5 text-sm text-text-weak">{children}</p>
    </div>
);

const StructureStep: React.FC<{
    structure: CurriculumTemplateStructurePreview;
    hasProcedure: boolean;
    options: CurriculumTemplateApplyOptions;
    onChange: (options: CurriculumTemplateApplyOptions) => void;
}> = ({ structure, hasProcedure, options, onChange }) => {
    const chosenLevels = options.levelNames ?? [];
    const toggleLevel = (name: string, on: boolean) =>
        onChange({ ...options, levelNames: on ? [...chosenLevels, name] : chosenLevels.filter((l) => l !== name) });

    return (
        <div className="space-y-4">
            <StepIntro title="Grades and structure">
                Where the {structure.qualificationName} sits in your organisation, and which grades to set up. Every grade gets a
                qualification module per subject and term.
            </StepIntro>
            <Checkbox
                label={<span className="font-medium text-text-strong">Set up grades and qualification modules</span>}
                hint={`Faculty, school, ${structure.qualificationName} and the grades below.`}
                checked={options.createStructure}
                onChange={(e) => onChange({ ...options, createStructure: e.target.checked })}
            />
            {structure.existingQualificationCount > 0 && (
                <Banner tone="warning">
                    {`This organisation already has ${structure.existingQualificationCount} qualification(s). If they come from the SCADSYS sync, untick this: the template matches existing rows by name, and the sync would not recognise the template's rows, so you could end up with duplicates.`}
                </Banner>
            )}
            {options.createStructure && (
                <>
                    <div className="grid gap-3 sm:grid-cols-2">
                        <Field label="Faculty" required hint="An existing faculty with this name is reused.">
                            <Input list="wizard-faculties" value={options.facultyName ?? ""} onChange={(e) => onChange({ ...options, facultyName: e.target.value })} />
                            <datalist id="wizard-faculties">
                                {structure.existingFaculties.map((f) => (
                                    <option key={f} value={f} />
                                ))}
                            </datalist>
                        </Field>
                        <Field label="School" required hint="An existing school in that faculty is reused.">
                            <Input list="wizard-schools" value={options.schoolName ?? ""} onChange={(e) => onChange({ ...options, schoolName: e.target.value })} />
                            <datalist id="wizard-schools">
                                {structure.existingSchools.map((s) => (
                                    <option key={s} value={s} />
                                ))}
                            </datalist>
                        </Field>
                    </div>
                    <div>
                        <div className="mb-1 text-sm font-medium text-text">Grades</div>
                        <div className="flex flex-wrap gap-4">
                            {structure.levels.map((level) => (
                                <Checkbox
                                    key={level.name}
                                    label={level.willCreate ? level.name : `${level.name} (existing)`}
                                    checked={chosenLevels.includes(level.name)}
                                    onChange={(e) => toggleLevel(level.name, e.target.checked)}
                                />
                            ))}
                        </div>
                    </div>
                    {hasProcedure && (
                        <Checkbox
                            label="Assign the calculation procedures to every module"
                            hint="Each module gets the division procedures and the term procedure. A module that already has an active procedure writing the same mark is left alone."
                            checked={options.assignProcedure}
                            onChange={(e) => onChange({ ...options, assignProcedure: e.target.checked })}
                        />
                    )}
                </>
            )}
        </div>
    );
};

const SubjectsStep: React.FC<{
    subjects: CurriculumTemplatePreviewItem[];
    options: CurriculumTemplateApplyOptions;
    onChange: (options: CurriculumTemplateApplyOptions) => void;
}> = ({ subjects, options, onChange }) => {
    const excluded = options.excludedSubjects ?? [];
    const added = options.additionalSubjects ?? [];
    const [name, setName] = React.useState("");
    const duplicate = [...subjects.map((s) => s.name), ...added].some((s) => s.trim().toLowerCase() === name.trim().toLowerCase());

    const add = () => {
        if (!name.trim() || duplicate) return;
        onChange({ ...options, additionalSubjects: [...added, name.trim()] });
        setName("");
    };

    return (
        <div className="space-y-4">
            <StepIntro title="Subjects">
                Untick the template subjects your school does not offer, and add your own. A subject that already exists is reused.
            </StepIntro>
            <div className="grid gap-2 sm:grid-cols-2">
                {subjects.map((s) => (
                    <Checkbox
                        key={s.name}
                        label={s.willCreate ? s.name : `${s.name} (existing)`}
                        checked={!excluded.includes(s.name)}
                        onChange={(e) =>
                            onChange({
                                ...options,
                                excludedSubjects: e.target.checked ? excluded.filter((x) => x !== s.name) : [...excluded, s.name],
                            })
                        }
                    />
                ))}
            </div>
            <div>
                <div className="mb-1 text-sm font-medium text-text">Your own subjects</div>
                {added.length > 0 && (
                    <ul className="mb-2 flex flex-wrap gap-1.5">
                        {added.map((s) => (
                            <li key={s} className="flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs text-text">
                                {s}
                                <button
                                    type="button"
                                    aria-label={`Remove ${s}`}
                                    className="text-text-weak hover:text-danger-text"
                                    onClick={() => onChange({ ...options, additionalSubjects: added.filter((x) => x !== s) })}
                                >
                                    &times;
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="flex gap-2">
                    <Input
                        value={name}
                        placeholder="e.g. Computer Applications Technology"
                        onChange={(e) => setName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                add();
                            }
                        }}
                        error={duplicate && name.trim() ? "That subject is already in the list." : undefined}
                    />
                    <Button variant="outline-primary" icon={["fad", "plus"]} onClick={add} disabled={!name.trim() || duplicate}>
                        Add
                    </Button>
                </div>
            </div>
        </div>
    );
};

const TermsStep: React.FC<{ options: CurriculumTemplateApplyOptions; onChange: (options: CurriculumTemplateApplyOptions) => void }> = ({
    options,
    onChange,
}) => {
    const cycles = options.cycles ?? [];
    const update = (i: number, change: Partial<CurriculumTemplateCycleOption>) =>
        onChange({ ...options, cycles: cycles.map((c, j) => (j === i ? { ...c, ...change } : c)) });

    return (
        <div className="space-y-4">
            <StepIntro title="Terms">
                Your school&apos;s terms, in order - three or four, or however many you use (up to {MAX_TERMS}). Only the day and month
                matter: the terms are laid down in the academic year being set up.
            </StepIntro>
            <div className="space-y-2">
                {cycles.map((c, i) => (
                    <div key={i} className="grid items-end gap-2 sm:grid-cols-[1fr_10rem_10rem_auto]">
                        <Field label={i === 0 ? "Name" : ""}>
                            <Input value={c.name} aria-label={`Term ${i + 1} name`} onChange={(e) => update(i, { name: e.target.value })} />
                        </Field>
                        <Field label={i === 0 ? "Starts" : ""}>
                            <Input type="date" aria-label={`${c.name} starts`} value={c.startDate ?? ""} onChange={(e) => update(i, { startDate: e.target.value })} />
                        </Field>
                        <Field label={i === 0 ? "Ends" : ""}>
                            <Input type="date" aria-label={`${c.name} ends`} value={c.endDate ?? ""} onChange={(e) => update(i, { endDate: e.target.value })} />
                        </Field>
                        <IconButton
                            icon={["fad", "trash"]}
                            label={`Remove ${c.name || "term"}`}
                            variant="outline-danger"
                            disabled={cycles.length <= 1}
                            onClick={() => onChange({ ...options, cycles: cycles.filter((_, j) => j !== i) })}
                        />
                    </div>
                ))}
            </div>
            <Button
                variant="outline-primary"
                icon={["fad", "plus"]}
                disabled={cycles.length >= MAX_TERMS}
                onClick={() => onChange({ ...options, cycles: [...cycles, { name: `Term ${cycles.length + 1}`, startDate: "", endDate: "" }] })}
            >
                Add term
            </Button>
        </div>
    );
};

const METHOD_LABELS: Record<string, string> = {
    SimpleAverage: "the average of its assessments",
    Aggregate: "the total of its assessments' marks over their combined total",
    WeightedAverage: "a weighted average of its assessments",
};

const WeightingsStep: React.FC<{
    data: CurriculumTemplatePreview;
    options: CurriculumTemplateApplyOptions;
    onChange: (options: CurriculumTemplateApplyOptions) => void;
}> = ({ data, options, onChange }) => {
    const weights = options.divisionWeights ?? {};
    const total = data.divisions.reduce((sum, d) => sum + (weights[d.name] ?? 0), 0);
    const rules = data.structure?.choiceRuleNames ?? [];
    const excludedRules = options.excludedChoiceRules ?? [];
    const reused = data.procedures.some((p) => !p.willCreate);

    return (
        <div className="space-y-6">
            {data.divisions.length > 0 && (
                <div className="space-y-3">
                    <StepIntro title="Division weightings">
                        Each subject&apos;s term mark weights its divisions. A division&apos;s own mark is{" "}
                        {METHOD_LABELS[data.divisionMethod ?? ""] ?? "calculated from its assessments"}.
                    </StepIntro>
                    <div className="grid gap-3 sm:grid-cols-3">
                        {data.divisions.map((d) => (
                            <Field key={d.name} label={`${d.name} (%)`}>
                                <Input
                                    type="number"
                                    min={1}
                                    max={100}
                                    step="any"
                                    value={percent(weights[d.name])}
                                    onChange={(e) =>
                                        onChange({ ...options, divisionWeights: { ...weights, [d.name]: e.target.value === "" ? 0 : Number(e.target.value) / 100 } })
                                    }
                                />
                            </Field>
                        ))}
                    </div>
                    <p className={`text-sm ${Math.abs(total - 1) > 0.001 ? "text-danger-text" : "text-text-weak"}`}>
                        Total {percent(total)}%{Math.abs(total - 1) > 0.001 ? " - the weightings must add up to 100%." : ""}
                    </p>
                    {reused && (
                        <Banner tone="info">
                            Some of this template&apos;s calculation procedures already exist. Existing procedures are reused as they are and never
                            edited, so these weightings only apply to procedures created now. To change an existing one, publish a new version of
                            it under Calculation procedures.
                        </Banner>
                    )}
                </div>
            )}

            {rules.length > 0 && (
                <div className="space-y-3">
                    <StepIntro title="Subject choice rules">
                        The rules laid down for each grade, which Subject choices checks before saving. A grade that already has a rule keeps
                        its own version.
                    </StepIntro>
                    <div className="grid gap-2 sm:grid-cols-2">
                        {rules.map((name) => (
                            <Checkbox
                                key={name}
                                label={name}
                                checked={!excludedRules.includes(name)}
                                onChange={(e) =>
                                    onChange({
                                        ...options,
                                        excludedChoiceRules: e.target.checked ? excludedRules.filter((r) => r !== name) : [...excludedRules, name],
                                    })
                                }
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * The assessment plan: per division, the assessments every subject gets, what each is out of, and the terms it is set
 * in. It is stamped into every subject at once; a subject's own assessments are then changed on the marks screen.
 */
const AssessmentsStep: React.FC<{
    data: CurriculumTemplatePreview;
    options: CurriculumTemplateApplyOptions;
    onChange: (options: CurriculumTemplateApplyOptions) => void;
}> = ({ data, options, onChange }) => {
    const plan = options.assessmentPlan ?? [];
    const terms = options.cycles ?? [];
    const types = data.assessmentTypes.map((t) => t.name);
    const update = (i: number, change: Partial<CurriculumTemplatePlannedAssessment>) =>
        onChange({ ...options, assessmentPlan: plan.map((p, j) => (j === i ? { ...p, ...change } : p)) });
    const toggleTerm = (i: number, term: number) => {
        const current = plan[i].terms ?? [];
        update(i, { terms: current.includes(term) ? current.filter((t) => t !== term) : [...current, term].sort((a, b) => a - b) });
    };

    return (
        <div className="space-y-5">
            <StepIntro title="Assessment plan">
                The assessments every subject gets in each division, and the terms they are set in. Leave every term unticked to set an
                assessment in all terms. Teachers can rename, add or remove a subject&apos;s own assessments afterwards on the marks screen.
            </StepIntro>
            {data.divisions.map((division) => (
                <div key={division.name} className="space-y-2">
                    <div className="text-sm font-semibold text-text-strong">{division.name}</div>
                    {plan.map((p, i) =>
                        p.division !== division.name ? null : (
                            <div key={i} className="grid items-end gap-2 rounded-md border border-border p-2 lg:grid-cols-[1fr_10rem_6rem_auto_auto]">
                                <Field label="Name">
                                    <Input value={p.name} onChange={(e) => update(i, { name: e.target.value })} />
                                </Field>
                                <Field label="Type">
                                    <Select value={p.assessmentType} onChange={(e) => update(i, { assessmentType: e.target.value })}>
                                        {types.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </Select>
                                </Field>
                                <Field label="Out of">
                                    <Input type="number" min={1} value={p.total || ""} onChange={(e) => update(i, { total: Number(e.target.value) })} />
                                </Field>
                                <div className="flex flex-wrap gap-2 pb-2" role="group" aria-label={`Terms for ${p.name}`}>
                                    {terms.map((t, index) => (
                                        <Checkbox
                                            key={index}
                                            label={t.name || `Term ${index + 1}`}
                                            checked={(p.terms ?? []).includes(index + 1)}
                                            onChange={() => toggleTerm(i, index + 1)}
                                        />
                                    ))}
                                    {(p.terms ?? []).length === 0 && <span className="self-center text-xs text-text-weak">every term</span>}
                                </div>
                                <IconButton
                                    icon={["fad", "trash"]}
                                    label={`Remove ${p.name || "assessment"}`}
                                    variant="outline-danger"
                                    onClick={() => onChange({ ...options, assessmentPlan: plan.filter((_, j) => j !== i) })}
                                />
                            </div>
                        )
                    )}
                    <Button
                        variant="outline-primary"
                        size="sm"
                        icon={["fad", "plus"]}
                        onClick={() =>
                            onChange({
                                ...options,
                                assessmentPlan: [...plan, { division: division.name, name: "", assessmentType: types[0] ?? "", total: 50, terms: [] }],
                            })
                        }
                    >
                        Add to {division.name}
                    </Button>
                </div>
            ))}
        </div>
    );
};

/** "Test (out of 50, every term)", "Examination (out of 100, Term 2 and Term 4)". */
function describePlanned(p: CurriculumTemplatePlannedAssessment, terms: CurriculumTemplateCycleOption[]): string {
    const when = (p.terms ?? []).length === 0 ? "every term" : p.terms.map((t) => terms[t - 1]?.name ?? `Term ${t}`).join(" and ");
    return `${p.name} (out of ${p.total}, ${when})`;
}

const ReviewStep: React.FC<{ data: CurriculumTemplatePreview; options: CurriculumTemplateApplyOptions; validationError?: string }> = ({
    data,
    options,
    validationError,
}) => {
    const excluded = options.excludedSubjects ?? [];
    const subjects = [...data.subjects.map((s) => s.name).filter((s) => !excluded.includes(s)), ...(options.additionalSubjects ?? [])];
    const rules = (data.structure?.choiceRuleNames ?? []).filter((r) => !(options.excludedChoiceRules ?? []).includes(r));
    const cycles = options.cycles ?? [];
    const grades = options.levelNames ?? [];

    return (
        <div className="space-y-4">
            <StepIntro title="Review">Nothing has been created yet. Check the choices below, then apply the template.</StepIntro>
            {validationError ? (
                <Banner tone="danger">{validationError} Go back to that step to fix it.</Banner>
            ) : (
                <Banner tone="success">These choices are ready to apply.</Banner>
            )}
            <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-[10rem_1fr]">
                {data.structure && (
                    <>
                        <dt className="font-medium text-text-weak">Structure</dt>
                        <dd className="text-text">
                            {options.createStructure
                                ? `${data.structure.qualificationName} in ${options.schoolName} (${options.facultyName}), ${grades.join(", ") || "no grades"}; ${subjects.length * cycles.length * grades.length} qualification modules at most${options.assignProcedure ? ", with procedures assigned" : ""}.`
                                : "Not set up - only the vocabulary, terms and procedures are created."}
                        </dd>
                    </>
                )}
                <dt className="font-medium text-text-weak">Subjects ({subjects.length})</dt>
                <dd className="text-text">{subjects.join(", ")}</dd>
                <dt className="font-medium text-text-weak">Terms ({cycles.length})</dt>
                <dd className="text-text">
                    {cycles.map((c) => `${c.name} (${shortDate(c.startDate)} - ${shortDate(c.endDate)})`).join(", ")}
                </dd>
                {data.divisions.length > 0 && (
                    <>
                        <dt className="font-medium text-text-weak">Term mark</dt>
                        <dd className="text-text">
                            {data.divisions.map((d) => `${d.name} ${percent(options.divisionWeights?.[d.name])}%`).join(" · ")}
                        </dd>
                    </>
                )}
                {data.divisions.length > 0 && data.structure && (
                    <>
                        <dt className="font-medium text-text-weak">Assessments</dt>
                        <dd className="text-text">
                            {(options.assessmentPlan ?? []).length === 0
                                ? "None - teachers add their own on the marks screen."
                                : data.divisions
                                      .map((d) => ({ d, items: (options.assessmentPlan ?? []).filter((p) => p.division === d.name) }))
                                      .filter((x) => x.items.length > 0)
                                      .map((x) => `${x.d.name}: ${x.items.map((p) => describePlanned(p, cycles)).join(", ")}`)
                                      .join(" · ")}
                        </dd>
                    </>
                )}
                {(data.structure?.choiceRuleNames?.length ?? 0) > 0 && (
                    <>
                        <dt className="font-medium text-text-weak">Choice rules ({rules.length})</dt>
                        <dd className="text-text">{rules.join(" · ") || "None"}</dd>
                    </>
                )}
                <dt className="font-medium text-text-weak">Also created</dt>
                <dd className="text-text">
                    {[...data.assessmentTypes.map((t) => t.name), ...data.markTypes.map((m) => m.name)].join(", ")}
                    {data.procedures.length > 0 ? `, and ${data.procedures.length} calculation procedures` : ""}
                </dd>
            </dl>
        </div>
    );
};

/** What a template contains, for someone who may look but not apply. */
const TemplateContents: React.FC<{ data: CurriculumTemplatePreview }> = ({ data }) => {
    const sections: Array<[string, CurriculumTemplatePreviewItem[]]> = [
        ["Subjects", data.subjects],
        ["Assessment types", data.assessmentTypes],
        ["Mark types", data.markTypes],
        ["Terms", data.cycles],
        ["Procedures", data.procedures],
    ];
    return (
        <div className="space-y-3 text-sm">
            {sections.map(([title, items]) => (
                <div key={title}>
                    <div className="text-xs font-semibold uppercase tracking-wider text-text-weak">{title}</div>
                    <p className="mt-0.5 text-text">{items.map((i) => i.name).join(", ") || "None"}</p>
                </div>
            ))}
            {data.divisions.length > 0 && (
                <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-text-weak">Term mark</div>
                    <p className="mt-0.5 text-text">{data.divisions.map((d) => `${d.name} ${Math.round(d.weight * 100)}%`).join(" · ")}</p>
                </div>
            )}
        </div>
    );
};

// ------------------------------------------------------------------ result

const ApplyResult: React.FC<{ displayName: string; result: CurriculumTemplateApplyResult; onClose: () => void }> = ({
    displayName,
    result,
    onClose,
}) => {
    const createdCount =
        result.createdSubjects.length + result.createdAssessmentTypes.length + result.createdMarkTypes.length + result.createdCycles.length;
    const structure = result.structure;
    const divisionNames = [...result.createdDivisions, ...result.skippedDivisions];

    return (
        <Modal
            isOpen
            title={`${displayName} applied`}
            icon={["fad", "circle-check"]}
            size="md"
            onClose={onClose}
            footer={
                <ModalFooter>
                    <Button variant="primary" onClick={onClose}>
                        Done
                    </Button>
                </ModalFooter>
            }
        >
            <ModalBody>
                <Banner tone="success">
                    {createdCount > 0
                        ? `Created ${result.createdSubjects.length} subjects, ${result.createdAssessmentTypes.length} assessment types, ${result.createdMarkTypes.length} mark types and ${result.createdCycles.length} terms.`
                        : "Every subject, type and term from this template already existed."}
                    {result.procedures.some((p) => !p.alreadyExisted)
                        ? ` Published ${result.procedures.filter((p) => !p.alreadyExisted).length} calculation procedures (division marks and the weighted term mark).`
                        : ""}
                </Banner>

                {structure && (
                    <div className="mt-4 space-y-1 text-sm text-text">
                        <div className="text-xs font-semibold uppercase tracking-wider text-text-weak">Grade structure</div>
                        <p>
                            {structure.qualificationName} at {structure.schoolName} ({structure.facultyName})
                            {structure.facultyCreated || structure.schoolCreated || structure.qualificationCreated
                                ? " was set up."
                                : " already existed and was reused."}
                        </p>
                        <p>
                            {structure.createdQualificationModules} qualification modules created
                            {structure.skippedQualificationModules ? `, ${structure.skippedQualificationModules} already existed` : ""}
                            {structure.createdDivisionGroups > 0 && `, with ${structure.createdDivisionGroups} division groups inside them`}.
                            {structure.createdAssignments > 0 && ` ${structure.createdAssignments} procedure assignments were made.`}
                        </p>
                        {(structure.createdAssessments ?? 0) > 0 && <p>{structure.createdAssessments} assessments were laid out across the subjects and terms.</p>}
                        {(structure.createdChoiceRules ?? 0) > 0 && (
                            <p>
                                {structure.createdChoiceRules}{" "}
                                <Link to="/enrollment/subject-choice-rules" className="text-primary underline" onClick={onClose}>
                                    subject choice rules
                                </Link>{" "}
                                were added across the grades.
                            </p>
                        )}
                    </div>
                )}

                <div className="mt-4 space-y-2">
                    <div className="text-xs font-semibold uppercase tracking-wider text-text-weak">Next steps</div>
                    {structure ? (
                        <>
                            <p className="text-sm text-text">
                                <Link to="/enrollment/subject-choices" className="text-primary underline" onClick={onClose}>
                                    Choose subjects
                                </Link>{" "}
                                for each grade&apos;s students.
                            </p>
                            <p className="text-sm text-text">
                                <Link to="/activities" className="text-primary underline" onClick={onClose}>
                                    Build the assessments
                                </Link>{" "}
                                {divisionNames.length > 0
                                    ? `inside each subject's ${divisionNames.join(", ").replace(/, ([^,]*)$/, " and $1")} divisions.`
                                    : "inside each subject."}
                            </p>
                            {structure.createdAssignments === 0 && (
                                <p className="text-sm text-text">
                                    <Link to="/assignments" className="text-primary underline" onClick={onClose}>
                                        Assign the calculation procedure
                                    </Link>{" "}
                                    to the modules that should calculate a term mark.
                                </p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-text">
                            <Link to="/assignments" className="text-primary underline" onClick={onClose}>
                                Assign the calculation procedure
                            </Link>{" "}
                            to your qualification modules.
                        </p>
                    )}
                    <p className="text-sm text-text">
                        Review{" "}
                        <Link to="/grading" className="text-primary underline" onClick={onClose}>
                            grade scales
                        </Link>{" "}
                        if you want symbols (A, B, C...) shown alongside percentages.
                    </p>
                </div>
            </ModalBody>
        </Modal>
    );
};

// ------------------------------------------------------------------ helpers

/** Dates arrive as full timestamps; the date inputs want yyyy-mm-dd. */
function normalise(options: CurriculumTemplateApplyOptions): CurriculumTemplateApplyOptions {
    return {
        ...options,
        excludedSubjects: options.excludedSubjects ?? [],
        additionalSubjects: options.additionalSubjects ?? [],
        excludedChoiceRules: options.excludedChoiceRules ?? [],
        levelNames: options.levelNames ?? [],
        cycles: (options.cycles ?? []).map((c) => ({ ...c, startDate: c.startDate?.slice(0, 10) ?? "", endDate: c.endDate?.slice(0, 10) ?? "" })),
    };
}

/** The obvious problems on the step on screen, caught before Next; the server checks everything again on save. */
function clientProblem(step: StepKey, options: CurriculumTemplateApplyOptions | null, data?: CurriculumTemplatePreview): string | undefined {
    if (!options || !data) return undefined;
    if (step === "structure" && data.structure && options.createStructure) {
        if (!options.facultyName?.trim() || !options.schoolName?.trim()) return "Name the faculty and school.";
        if (!options.levelNames?.length) return "Choose at least one grade.";
    }
    if (step === "subjects") {
        const kept = data.subjects.filter((s) => !(options.excludedSubjects ?? []).includes(s.name)).length;
        if (kept + (options.additionalSubjects?.length ?? 0) === 0) return "Choose at least one subject.";
    }
    if (step === "terms") {
        const cycles = options.cycles ?? [];
        const missing = cycles.find((c) => !c.name?.trim() || !c.startDate || !c.endDate);
        if (missing) return "Give every term a name, a start date and an end date.";
        const backwards = cycles.find((c) => (c.startDate ?? "") > (c.endDate ?? ""));
        if (backwards) return `${backwards.name} ends before it starts.`;
    }
    if (step === "assessments") {
        const plan = options.assessmentPlan ?? [];
        if (plan.some((p) => !p.name?.trim())) return "Give every assessment a name.";
        const bad = plan.find((p) => !(p.total > 0));
        if (bad) return `${bad.name} needs a positive "out of".`;
        const termCount = options.cycles?.length ?? 0;
        const missingTerm = plan.find((p) => (p.terms ?? []).some((t) => t > termCount));
        if (missingTerm) return `${missingTerm.name} is set in a term you removed; untick it.`;
    }
    return undefined;
}

const percent = (fraction?: number | null) => (fraction === undefined || fraction === null ? "" : String(Math.round(fraction * 10000) / 100));

const shortDate = (iso?: string | null) =>
    iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "?";

export default CurriculumTemplateWizard;
